import { Cloud, Droplets, Sparkles, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMemo } from "react";

interface ConnectionWeatherBannerProps {
  matches: Array<{
    match_source: string;
    isOnline?: boolean;
    created_at: string;
    source_context?: Record<string, unknown> | null;
  }>;
  className?: string;
}

export const ConnectionWeatherBanner = ({ matches, className }: ConnectionWeatherBannerProps) => {
  const weather = useMemo(() => {
    const onlineMatches = matches.filter((m) => m.isOnline).length;
    const globalMatches = matches.filter((m) => m.match_source === "global_connect").length;
    const exhibitionMatches = matches.filter((m) => m.match_source === "exhibition").length;
    const recentMatches = matches.filter((m) => {
      const matchDate = new Date(m.created_at);
      const daysAgo = (Date.now() - matchDate.getTime()) / (1000 * 60 * 60 * 24);
      return daysAgo <= 7;
    }).length;

    // Calculate "spark humidity" (percentage of active matches)
    const sparkHumidity = matches.length > 0 
      ? Math.round((onlineMatches / matches.length) * 100) 
      : 0;

    // Calculate "chance of sparks" (recent activity + online presence)
    const chanceOfSparks = Math.min(100, Math.round(
      (onlineMatches * 30 + recentMatches * 20 + (matches.length > 0 ? 20 : 0))
    ));

    // Determine "energy" type
    let energy = "Breezy";
    if (exhibitionMatches > 0) energy = "Romantic";
    if (globalMatches > 0) energy = "Global";
    if (onlineMatches >= 3) energy = "High Voltage";

    // Generate poetic forecast
    const forecasts = [
      `High spark humidity with your ${globalMatches > 0 ? 'Global' : 'matches'} — ${chanceOfSparks}% chance of deep chats tonight`,
      `${sparkHumidity}% spark humidity, ${chanceOfSparks}% chance of sparks, ${energy} flirt energy`,
      `Tonight's connection forecast: ${chanceOfSparks}% chance of sparks, ${energy} energy flowing`,
      `${onlineMatches} active sparks online — ${chanceOfSparks}% chance of meaningful connections`,
    ];

    return {
      forecast: forecasts[Math.floor(Math.random() * forecasts.length)],
      sparkHumidity,
      chanceOfSparks,
      energy,
      icon: onlineMatches > 0 ? Zap : Cloud,
    };
  }, [matches]);

  if (matches.length === 0) return null;

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl p-4 mb-4",
        "bg-gradient-to-r from-primary/10 via-primary/5 to-transparent",
        "border border-primary/20 backdrop-blur-sm",
        "animate-fade-in",
        className
      )}
    >
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5 animate-pulse" />
      
      <div className="relative flex items-center gap-3">
        <div className="shrink-0 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
          <weather.icon className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground leading-tight">
            Connection Weather Today
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {weather.forecast}
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-1 text-xs font-bold text-primary">
          <Sparkles className="w-3 h-3" />
          <span>{weather.chanceOfSparks}%</span>
        </div>
      </div>
    </div>
  );
};

